import React, { useState } from 'react';
import apiClient from '../config/axios';
import { track } from '../utils/analytics';
import { INVITES_CHANGED_EVENT } from './invitesEvents';

/**
 * Pending campaign invites with Accept and Decline (issue 500 §4), shared by
 * the account page's games tab and the top of the Campaigns page (issue 1536:
 * a first-time player follows the Campaigns page's own text and must find the
 * invite there too).
 *
 * Deciding PUTs the decision, waits for the caller's `onDecided` (each page
 * reloads what it shows) and tells the layout to recount the badge through
 * one window event, so the nav link and every list agree.
 */

const ROLE_LABEL = { owner: 'DM', co_gm: 'Co-DM', player: 'Player' };

export function roleLabel(role) {
  return ROLE_LABEL[role] || role;
}

export function personLabel(p) {
  if (!p) return 'somebody';
  if (p.handle) return `@${p.handle}`;
  return p.display_name || 'somebody';
}

export default function InviteList({ invites, onDecided }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const decide = async (invite, decision) => {
    setBusy(invite.id);
    setError(null);
    try {
      await apiClient.put(`/api/users/me/invites/${invite.id}`, { decision });
      if (decision === 'accepted') track('invite-accepted');
      if (onDecided) await onDecided(invite, decision);
      window.dispatchEvent(new Event(INVITES_CHANGED_EVENT));
    } catch (err) {
      setError('That did not go through. Try again in a moment.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {error && <p className="form-error" role="alert">{error}</p>}
      <ul className="mt-3 divide-y" style={{ borderColor: 'var(--border)' }} data-testid="invite-list">
        {invites.map((inv) => (
          <li key={inv.id} className="py-3 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[12rem]">
              <p className="font-medium text-primary">{inv.campaign.name}</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {personLabel(inv.invited_by)} invited you as {roleLabel(inv.role).toLowerCase()}
                {inv.campaign.system_name ? ` · ${inv.campaign.system_name}` : ''}
              </p>
            </div>
            <button type="button" className="btn btn-primary" disabled={busy === inv.id}
              onClick={() => decide(inv, 'accepted')} aria-label={`Accept invitation to ${inv.campaign.name}`}>
              Accept
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy === inv.id}
              onClick={() => decide(inv, 'declined')} aria-label={`Decline invitation to ${inv.campaign.name}`}>
              Decline
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}
